import type { SchemaData } from '@lemonade-hq/chat-engine';
import { getMergedSchemaForStep, SessionStatus } from '@lemonade-hq/chat-engine';
import type {
  OnInvalidActionProps,
  WizardChatEnginePersistenceProps,
  WizardExtendedStepWithPersistenceAndId,
} from '@lemonade-hq/chat-wizard-client-base';
import {
  spreadStepAnswersIntoSingleObject,
  useAnswersResync,
  useConditionalItem,
  useStaleness,
  useWizardActions,
  useWizardAnswers,
  useWizardAssets,
  useWizardNavigationContext,
  useWizardNavigationHelpers,
} from '@lemonade-hq/chat-wizard-client-base';
import type { GenericSchemaDataValue } from '@lemonade-hq/maschema-schema';
import { useI18nContext } from '@lemonade-hq/nuiza';
import type { Maybe, Seconds } from '@lemonade-hq/ts-helpers';
import {
  MenuOptionType,
  useRedirectContext,
  useSetDialogLoading,
  useUniclientTrackingContext,
} from '@lemonade-hq/uniclient-app';
import type {
  UniclientAction,
  UniclientComponent,
  UniclientMessageFrame,
  UniclientNativeAction,
  UniclientStep,
  UniclientStepPresentation,
} from '@lemonade-hq/uniclient-chat';
import { defaultHeaderActions, interpolate, isUniclientFrameOfType } from '@lemonade-hq/uniclient-chat';
import type { Actions } from '@lemonade-hq/uniclient-mobile-bridge';
import { invokeAction, isNativeApp, nativeApp } from '@lemonade-hq/uniclient-mobile-bridge';
import {
  drawerBottomSafeAreaHeightVar,
  drawerTopSafeAreaHeightVar,
  Flex,
  headerHeightVar,
  Layout,
  spacing,
  useDelayedValue,
  useTheme,
  useToast,
  useViewportSize,
} from '@lemonade-hq/uniclient-theme';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { setElementVars } from '@vanilla-extract/dynamic';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { AnimatePresence, motion } from 'motion/react';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { COMPONENT_ID, COMPONENTS_MAP } from '../adapters/index';
import type { UniclientComponentAdapterFunctionalComponent } from '../adapters/types';
import { FRAME_TRANSITION_DURATION, FRAME_TYPE_PAGE_TRANSITION_DURATION } from '../animators/durations';
import { useEnterAnimationStagger } from '../animators/useEnterAnimationStagger';
import { WizardFrameStepAnimator } from '../animators/WizardFrameStepAnimator';
import { useUniclientExtension } from '../hooks/uniclientExtension/useUniclientExtension';
import { useAsyncTriggerCallback } from '../hooks/useAsyncTriggerCallback';
import { useGetUniclientPersistence } from '../hooks/useGetUniclientPersistence';
import { useGetUrlParams } from '../hooks/useGetUrlParams';
import { useIOSAutoScrollToInput } from '../hooks/useIOSAutoScrollToInput';
import { usePreloadWizardComponents } from '../hooks/usePreloadWizardComponents';
import { useTransitionBetweenScripts } from '../hooks/useTransitionBetweenScripts';
import { useUserAuthDetails } from '../hooks/useUserAuthDetails';
import { useWizardComponents } from '../hooks/useWizardComponents';
import { useWizardContextMenu } from '../hooks/useWizardContextMenu';
import { useWizardDialogs } from '../hooks/useWizardDialogs';
import { useWizardErrors } from '../hooks/useWizardErrors';
import { useWizardHistory } from '../hooks/useWizardHistory';
import { useWizardHistoryEdit } from '../hooks/useWizardHistoryEdit';
import type { HeaderMenuActionsProps } from '../hooks/useWizardMenu';
import { useWizardHeaderActions, useWizardMenuPredefinedActions } from '../hooks/useWizardMenu';
import { useWizardResolvedSteps } from '../hooks/useWizardResolvedSteps';
import { useWizardScrollSnapController } from '../hooks/useWizardScrollSnapController';
import { ScrollSnapItem } from '../hooks/useWizardScrollSnapController.css';
import { useWizardStepTracking } from '../hooks/useWizardStepTracking';
import { useWizardToast } from '../hooks/useWizardToast';
import { useWizardUiAttributes } from '../hooks/useWizardUiAttributes';
import { useWizardPersistenceContext } from '../persistence/wizard_persistence.context';
import { useAutoTriggerActionContext } from '../providers/AutoTriggerActionProvider';
import { changeValuesByStrategy } from '../utils/changeValuesByStrategy';
import { checkEveryComponentOfType, isSingleComponentOfType } from '../utils/componentTypeUtils';
import { WIZARD_COMPONENT_CONTAINER_ELEMENT_ID } from '../utils/consts';
import { getMappedInterpolation } from '../utils/getMappedInterpolation';
import { getTitleLength } from '../utils/getTitleLength';
import { buildPayload, makeAsyncCall } from '../utils/httpRequest';
import { FreeTextInputController } from './components/freeTextInput/FreeTextInputController';
import { LandingPage } from './components/LandingPage/LandingPage';
import { useFreeTextInputState } from './hooks/useFreeTextInputState';
import { MOBILE_BOTTOM_SAFE_AREA, MOBILE_HEADER_SAFE_AREA } from './layout';
import { TYPING_TIMEOUT_MULTIPLIER } from './TitleContent';
import type { MdLgColumns } from './utils/themeColumns';
import { DEFAULT_COLUMNS, DEFAULT_COLUMNS_FOR_CONTENT, getGridColumn } from './utils/themeColumns';
import * as styles from './Wizard.css';
import { WizardActions } from './WizardActions';
import { WizardAvatar } from './WizardAvatar';
import { WizardBackgroundImage } from './WizardBackgroundImage';
import { WizardComponent } from './WizardComponent';
import { DISCLAIMER_ANIMATION_DELAY, WizardDisclaimer } from './WizardDisclaimer';
import { WizardFooter } from './WizardFooter';
import { footerContainer } from './WizardFooter.css';
import { WizardFrame } from './WizardFrame';
import { WizardHeaderActions, WizardHeaderLogo } from './WizardHeader';
import { HEADER_HEIGHT } from './WizardHeader.css';
import { WizardHeaderSide } from './WizardHeaderSide';
import { WizardHistory } from './WizardHistory';
import { WizardLayoutContainer } from './WizardLayoutContainer';
import { WizardMain } from './WizardMain';
import { WizardMobileHeader } from './WizardMobileHeader';
import { WizardSurvey } from './WizardSurvey';

setElementVars(document.body, {
  [headerHeightVar]: `${HEADER_HEIGHT.sm}px`,
  [drawerTopSafeAreaHeightVar]: `${MOBILE_HEADER_SAFE_AREA}px`,
  [styles.topSafeAreaHeightVar]: `${MOBILE_HEADER_SAFE_AREA}px`,
  [drawerBottomSafeAreaHeightVar]: `${MOBILE_BOTTOM_SAFE_AREA}px`,
  [styles.bottomSafeAreaHeightVar]: `${MOBILE_BOTTOM_SAFE_AREA}px`,
});

const AVATAR_ANIMATION_DELAY: Seconds = 0.4;
const AVATAR_ANIMATION_DURATION: Seconds = 0.2;

const wizardActionsAnimationVariants = {
  hidden: {
    y: '100%',
    opacity: 0,
    transition: {
      opacity: { duration: 0.2, ease: 'easeIn', delay: 0.1 },
      y: { duration: 0.3, ease: 'easeInOut' },
    },
  },
  visible: {
    y: '0%',
    opacity: 1,
    transition: {
      opacity: { duration: 0.1, ease: 'easeOut', delay: 0.1 },
      y: { duration: 0.3, ease: 'easeInOut' },
    },
  },
} as const;

interface WizardProps {
  readonly scriptName: WizardChatEnginePersistenceProps<UniclientStepPresentation>['scriptName'];
}

export const Wizard: React.FC<WizardProps> = ({ scriptName: scriptNameFromPath }) => {
  const i18nContext = useI18nContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const presentationLock = useRef<boolean>(false);
  const nav = useWizardNavigationContext();
  const { redirectFn, webRedirectFn, isUrlInternal, isUrlSameScript } = useRedirectContext();
  const getUniclientPersistence = useGetUniclientPersistence();
  const getUrlParams = useGetUrlParams();
  const {
    auth,
    assets,
    steps,
    answersSchema,
    persistedAnswersByStep,
    isInitializing,
    isSubmitting,
    submitAnswers,
    truncateObsoleteLocalSteps,
    error: persistenceError,
    isSubmittingLLMRequest,
    isErrorFromLLMRequest,
    sessionStatus,
    sessionId,
    initSession,
  } = useWizardPersistenceContext<UniclientStep>();
  const { currentStepIndex, back } = nav;
  const currentStep = steps[currentStepIndex] as Maybe<WizardExtendedStepWithPersistenceAndId<UniclientStep>>;
  const { data: currentStepData, presentation: currentStepPresentation } = currentStep ?? {};
  const {
    frame: currentStepFrameValues,
    component: currentStepComponentValue,
    dialogs: currentStepDialogs,
  } = currentStepPresentation ?? {};
  const { isMobileViewport, isBelowDesktopViewport } = useViewportSize();
  const { open: openToast } = useToast();
  const { s } = useI18nContext();
  const {
    trackError,
    trackAnswers,
    trackEvent,
    trackAction,
    updateIdentity,
    trackEdit,
    trackSuccess,
    trackInvalidAnswer,
    getSecureAnswers,
  } = useUniclientTrackingContext();
  const { setTheme, theme } = useTheme();

  const {
    isTransitioningBetweenScripts,
    isTransitioningForward,
    isTransitioningToSameScript,
    createTransitionTriggerCallback,
    transitionTriggerCallback,
    scriptName,
    lastTransitioningFrameType,
  } = useTransitionBetweenScripts({
    scriptNameFromPath,
    isInitializing,
    presentationLock,
  });

  useIOSAutoScrollToInput();
  useUserAuthDetails({ userPublicId: auth?.userPublicId });

  // UI ATTRIBUTES
  const { uiAttrs, updateUiAttrs } = useWizardUiAttributes();

  // ANSWERS, VALIDATIONS AND DATA FLOW
  const mergedSchema = useMemo(() => {
    return getMergedSchemaForStep(answersSchema, currentStepData);
  }, [answersSchema, currentStepData]);

  const {
    currentStepAnswers,
    truncateObsoleteLocalAnswers,
    clearLocalAnswers,
    updateAnswers,
    hasPendingAnswersUpdate,
    hasFailingValidations,
    hasFailingStrictSubmissionValidations,
    failingValidations,
    finishedInitiatingCurrentStep,
  } = useWizardAnswers({
    persistedAnswersByStep,
    currentStepIndex: currentStepIndex,
    dataConfiguration: currentStepData,
    answersSchema: mergedSchema,
    changeDependenciesInterpolateFunction: interpolate,
    scriptName: scriptName.current,
    stepId: currentStep?.id,
    isTransitioningBetweenScripts,
    onInvalidAnswer: trackInvalidAnswer,
  });

  const stepAnswers = useMemo(() => ({ ...currentStepAnswers, ...uiAttrs }), [currentStepAnswers, uiAttrs]);
  const allAnswers = useMemo(
    () => ({ ...uiAttrs, ...spreadStepAnswersIntoSingleObject(persistedAnswersByStep) }),
    [persistedAnswersByStep, uiAttrs],
  );

  // CONDITIONAL RENDERING
  const currentStepFrame = useConditionalItem({
    items: currentStepFrameValues,
    answers: stepAnswers,
    presentationLock,
  });

  const delayedCurrentStepId = useDelayedValue(
    isTransitioningBetweenScripts ? undefined : currentStep?.id,
    currentStep?.id,
    FRAME_TRANSITION_DURATION,
  );

  let delayedCurrentStepFrameType = useDelayedValue(
    isTransitioningBetweenScripts ? undefined : currentStepFrame?.type ?? (steps.length === 0 ? undefined : 'message'),
    currentStepFrame?.type,
    FRAME_TRANSITION_DURATION,
  );
  delayedCurrentStepFrameType ??= currentStepFrame?.type;
  lastTransitioningFrameType.current ??= delayedCurrentStepFrameType;
  const isCurrentStepFrameLandingPage = isUniclientFrameOfType(currentStepFrame, 'landingPage');

  // UI HELPERS
  const [createSubmissionTriggerCallback, submissionTriggerCallback] = useAsyncTriggerCallback();
  const historyRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const [actionsHidden, setActionsHidden] = useState(false);

  // COMPONENTS
  const {
    isLoading: isLoadingComponents,
    components,
    componentsProps,
  } = useWizardComponents({
    componentsMap: COMPONENTS_MAP,
    currentStepComponent: currentStepComponentValue as UniclientComponent<string>[],
    presentationLock,
    answers: { ...allAnswers, ...currentStepAnswers },
  });

  const shouldPreloadComponents =
    !import.meta.env.DEV && currentStepComponentValue != null && !isLoadingComponents && currentStepIndex > 0;

  usePreloadWizardComponents({
    shouldPreload: shouldPreloadComponents,
    componentMap: COMPONENTS_MAP,
  });

  const {
    ref: mainRef,
    disableScrollSnap,
    enableScrollSnap,
    scrollToContent,
    scrollToHistory,
  } = useWizardScrollSnapController({
    contentRef: frameRef,
    enabled: !isLoadingComponents && currentStepFrame?.type !== 'page',
  });

  const componentRef = useRef<HTMLDivElement>(null);

  const { showFreeTextInput, setShowFreeTextInput, handleClickOutside } = useFreeTextInputState({
    currentStepFrame,
  });

  useWizardStepTracking({
    answersSchema,
    currentStep,
    currentStepAnswers,
    nav,
    isSubmitting,
    scriptName: scriptName.current,
    sessionId,
    userPublicId: auth?.userPublicId,
    currentStepFrame,
    componentsType: componentsProps[0]?.type,
  });

  // ACTIONS & SUBMISSION
  const performActionRef = useRef<(action?: UniclientAction) => Promise<unknown>>(async () => await Promise.resolve());

  const {
    openDialog,
    openEditHistoryDialog,
    openStartOverDialog,
    closeDialog,
    openAskUsAnythingDialog,
    openSurveyDialog,
  } = useWizardDialogs({
    trackEvent,
    onActionRef: performActionRef,
    currentStepIndex,
    persistedAnswers: allAnswers,
    currentStepDialogs,
  });
  const { openContextMenu } = useWizardContextMenu({ onActionRef: performActionRef });
  useAnswersResync<UniclientAction>({
    currentStepData,
    answers: currentStepAnswers,
    performAction: performActionRef.current,
  });

  const { registerAutoTriggerAction } = useAutoTriggerActionContext(currentStepIndex);

  const onInvalidAction = useCallback(
    (invalidChatActionProps: OnInvalidActionProps) => {
      trackEvent('chat.invalid_action', {
        ...invalidChatActionProps,
        failingValidations,
        answers: getSecureAnswers(allAnswers),
      });
    },
    [trackEvent, failingValidations, allAnswers, getSecureAnswers],
  );

  const {
    hasNoChangeSinceSubmission,
    performAction,
    canSubmit,
    isLoading: isLoadingActions,
    setWaitingForInternalProcess,
    isWaitingForInternalProcess,
  } = useWizardActions<UniclientAction>({
    nav,
    currentStepIndex,
    hasPendingAnswersUpdate,
    hasFailingValidations,
    isSubmitting,
    isSubmissionEnabled: !hasFailingStrictSubmissionValidations && finishedInitiatingCurrentStep,
    currentStepAnswers,
    onInvalidAction,
    redirectFn: async (url: string) => {
      if (isUrlInternal(url) && !isCurrentStepFrameLandingPage) {
        presentationLock.current = true;
        await createTransitionTriggerCallback();
        disableScrollSnap([ScrollSnapItem.ScriptTransition]);
        enableScrollSnap(ScrollSnapItem.Submission);
        isTransitioningForward.current = true;
        lastTransitioningFrameType.current = delayedCurrentStepFrameType;
      }

      const isSameScript = isUrlSameScript(url);

      redirectFn(url);

      if (isSameScript) {
        isTransitioningToSameScript.current = true;
        const urlObject = new URL(url, window.location.origin);

        const urlParams = getUrlParams(urlObject);
        const { params } = getUniclientPersistence(urlObject);
        if (params == null) {
          return;
        }

        initSession({ ...params, ...urlParams, clearPreviousSession: true });
        clearLocalAnswers();
      }
    },
    onRedirect: (url: string) => trackEvent('chat.redirect_requested', { url }),
    customActions: {
      openAUADialog: openAskUsAnythingDialog,
      back: ({ levels }) => {
        const newCurrentIndexStep = back(levels) as number;
        truncateObsoleteLocalSteps(newCurrentIndexStep);
      },
      scroll: ({ toId, toAttribute, behavior = 'smooth' }) => {
        const elementId =
          toAttribute != null && typeof allAnswers[toAttribute] === 'string' ? allAnswers[toAttribute] : toId;

        if (elementId !== undefined && elementId !== '') {
          document.querySelector(`#${elementId}`)?.scrollIntoView({ behavior });
        }
      },
      scrollToHistory: () => {
        scrollToHistory();
      },
      nativeAction: async (action: UniclientNativeAction) => {
        if (isNativeApp()) {
          if (action.actionType === 'dismiss') {
            await nativeApp.dismiss(action.payload as Actions | undefined);
          } else {
            const actionFn = invokeAction('appAction', action.actionType);
            await actionFn(action.payload);
          }
        }
      },
      openDialog: ({ dialog }) => {
        openDialog(dialog);
      },
      closeDialog,
      openContextMenu: ({ items }) => {
        openContextMenu(items);
      },
      changeValues: ({ data }) => {
        const uiValues = changeValuesByStrategy(
          data.filter(item => 'uiAttribute' in item),
          uiAttrs,
        );

        const values = changeValuesByStrategy(
          data.filter(item => 'attribute' in item),
          currentStepAnswers,
          mergedSchema,
        );

        if (Object.keys(uiValues).length > 0) updateUiAttrs(uiValues);
        if (Object.keys(values).length > 0) updateAnswers(values);
      },
      openUrl: ({ url, downloadable = false, fileExtension }) => {
        if (isNativeApp()) {
          void nativeApp.openUrl({
            url,
            externalBrowser: !downloadable,
            downloadable,
            fileExtension,
            inAppBrowser: false,
          });
        } else if (url.startsWith('http')) {
          window.open(url, '_blank')?.focus();
        } else {
          webRedirectFn(url);
        }
      },
      copyText: ({ text }) => {
        void navigator.clipboard.writeText(text);
      },
      compose: async ({ actions }) => {
        for (const action of actions) {
          await performActionRef.current(action);
        }
      },
      asyncRequest: async request => {
        try {
          const body = buildPayload(request.payloadMap, { ...currentStepAnswers, ...request.initialValues });

          if (request.shouldSetGlobalLoading) {
            setWaitingForInternalProcess(true);
          }

          const response = await makeAsyncCall({
            url: request.url,
            payload: body,
            headers: request.headers,
            method: request.method,
            withCredentials: request.withCredentials,
          });

          if (!response.ok) throw new Error('Request failed');
          if (request.successMessage != null) {
            openToast({
              icon: 'checkmark-solid',
              title: request.successMessage,
              type: 'success',
            });
          }

          if (request.successAction != null) {
            await performActionRef.current(request.successAction);
          }
        } catch {
          const errorMessage = request.errorMessage ?? s('chat.error.general');
          openToast({
            icon: 'alert-solid',
            title: errorMessage,
            id: errorMessage,
          });

          if (request.errorAction != null) {
            await performActionRef.current(request.errorAction);
          }
        } finally {
          if (request.shouldSetGlobalLoading) {
            setWaitingForInternalProcess(false);
          }
        }
      },
      openToast: ({ toastType, type: _actionType, ...props }) => {
        openToast({ type: toastType, ...props });
      },
      changeTheme: ({ brand, scheme }) => {
        setTheme({
          brand: brand ?? theme.brand,
          scheme: scheme ?? theme.scheme,
        });
      },
    },
    onAction: action => {
      if (action?.type === 'resync' && action.skipHooks !== true) {
        const attributes = action.attributes ?? [];

        const answers = attributes.reduce(
          (acc, key) => ({ ...acc, [key]: get(currentStepAnswers, key) as GenericSchemaDataValue }),
          {} as SchemaData,
        );

        void trackEdit(answers);
      }
    },
    onSubmit: async ({ answers, action }) => {
      const scrollToTopIsEnabled = !action.disableScrollToTop;

      presentationLock.current = scrollToTopIsEnabled;
      if (scrollToTopIsEnabled && !isCurrentStepFrameLandingPage)
        disableScrollSnap([ScrollSnapItem.Main, ScrollSnapItem.Submission]);

      const submitResponse = await submitAnswers(answers, { resync: action.type === 'resync' });

      return submitResponse;
    },
    startSubmissionTransition: async ({ action, redirectUrl }) => {
      if (redirectUrl != null) {
        return;
      }

      const scrollToTopIsEnabled = !action.disableScrollToTop;

      if (scrollToTopIsEnabled && !isCurrentStepFrameLandingPage) {
        // wait for the frame to transition out before completing the submission
        await createSubmissionTriggerCallback();

        enableScrollSnap(ScrollSnapItem.Submission);
      }
    },
    onSubmitSuccess: ({ didAnswersChange, action }) => {
      if (didAnswersChange) {
        truncateObsoleteLocalAnswers();
      }

      if (action.tracking?.trackSubmissionSuccess) {
        trackSuccess();
      }

      presentationLock.current = false;
    },
    onSubmitFailure: () => {
      enableScrollSnap([ScrollSnapItem.Main, ScrollSnapItem.Submission]);
      presentationLock.current = false;
    },
    analytics: {
      trackEvent: trackAction,
      trackAnswers,
      updateIdentity,
    },
  });

  performActionRef.current = performAction;

  registerAutoTriggerAction(currentStepFrame?.action, performActionRef.current);

  // ERRORS & TOASTS

  const { setCustomError } = useWizardErrors({
    isSubmitting,
    currentStepIndex,
    persistenceError,
    isInitializing,
    hasNoChangeSinceSubmission,
    isErrorFromLLMRequest,
    onError: trackError,
  });

  // HISTORY EDIT
  const { onHistoryEdit, editingEntryId } = useWizardHistoryEdit({
    steps,
    currentStepIndex,
    back,
    disableScrollSnap,
    enableScrollSnap,
    truncateObsoleteLocalSteps,
    openEditHistoryDialog,
  });

  const onStartOver = useCallback(() => {
    openStartOverDialog(() => {
      nav.restart();
    });
  }, [openStartOverDialog, nav]);

  // HISTORY
  const resolvedSteps = useWizardResolvedSteps({ steps, answers: allAnswers });
  const { history, disabled: historyIsDisabled } = useWizardHistory({
    currentStepIndex,
    steps: resolvedSteps,
    isTransitioningOut: submissionTriggerCallback.current != null,
    isTransitioningBetweenScripts: isTransitioningBetweenScripts || transitionTriggerCallback.current != null,
  });

  const headerActions = useMemo(
    () =>
      ({
        [MenuOptionType.Edit]: {
          onAction: scrollToHistory,
          visible: !historyIsDisabled,
          disabled: history.length === 0,
          icon: 'edit',
        },
        [MenuOptionType.StartOver]: {
          onAction: onStartOver,
          visible: history.length > 0,
          icon: 'restart',
        },
        [MenuOptionType.Logout]: {
          visible: !isNativeApp(),
          icon: 'logout',
        },
        [MenuOptionType.AskUsAnything]: {
          onAction: openAskUsAnythingDialog,
          visible: true,
          icon: 'email',
        },
      }) as const satisfies HeaderMenuActionsProps,
    [scrollToHistory, historyIsDisabled, history.length, onStartOver, openAskUsAnythingDialog],
  );
  const predefinedActions = useWizardMenuPredefinedActions(headerActions, redirectFn);
  const headerFilteredActions = useWizardHeaderActions({
    currentStepFrame,
    defaultHeaderActions,
  });

  // BASE EFFECTS
  const componentAnimationDelay = useMemo(() => {
    return FRAME_TRANSITION_DURATION + TYPING_TIMEOUT_MULTIPLIER * getTitleLength(currentStepFrame?.title);
  }, [currentStepFrame?.title]);

  useWizardToast({
    toast: currentStepFrame?.toast,
    performActionRef,
    currentStepId: currentStep?.id,
    animationDelay: componentAnimationDelay,
  });
  useWizardAssets({ assets });
  useWizardNavigationHelpers({
    nav,
    isInitializing,
    steps,
    isBackDisabled: !isTransitioningBetweenScripts && (currentStepFrame?.lockHistory ?? false),
    zeroBased: true,
    isTransitioningBetweenScripts,
  });
  useEnterAnimationStagger({
    ref: containerRef,
    animate: Boolean(components.length),
    animationDelay: componentAnimationDelay,
  });
  useStaleness({
    stepData: currentStepData,
    currentStepIndex,
    performAction: performActionRef.current,
  });

  const isLoading = isLoadingActions || isLoadingComponents || isSubmittingLLMRequest;
  const isTransitioningOut =
    submissionTriggerCallback.current != null ||
    transitionTriggerCallback.current != null ||
    isTransitioningForward.current;
  const isSealed = sessionStatus === SessionStatus.Sealed;
  const isDisabled = isLoading || isSealed;

  useSetDialogLoading(isLoading);

  useUniclientExtension(
    {
      failingValidations,
      steps,
      answersSchema,
      persistedAnswersByStep,
      currentStepIndex,
      currentStepAnswers,
      uiAttrs,
    },
    {
      onAction: performActionRef.current,
    },
  );

  const hasFooter = currentStepFrame?.coInsurance != null && currentStepFrame.coInsurance.position === 'bottom';
  const pageFrameTransitionDelay =
    currentStepFrame?.type !== 'page' || currentStepFrame.title == null
      ? 0
      : FRAME_TYPE_PAGE_TRANSITION_DURATION + FRAME_TRANSITION_DURATION;

  const isAvatarVisible = useDelayedValue(
    isTransitioningBetweenScripts
      ? lastTransitioningFrameType.current === 'message'
      : delayedCurrentStepFrameType !== 'page',
    lastTransitioningFrameType.current != null,
    pageFrameTransitionDelay,
  );

  const disclaimer = currentStepFrame?.disclaimer;
  const hasActions =
    (currentStepFrame?.action != null && !currentStepFrame.action.hidden) ||
    (currentStepFrame?.secondaryAction != null && !currentStepFrame.secondaryAction.hidden);
  const showDisclaimerAfterActions = !isMobileViewport && hasActions;
  const showDisclaimerBeforeActions = (hasActions && isMobileViewport) || !hasActions;

  const wizardLayoutColumns = getGridColumn({
    md: DEFAULT_COLUMNS_FOR_CONTENT.md,
    lg: delayedCurrentStepFrameType === 'page' ? DEFAULT_COLUMNS.lg : DEFAULT_COLUMNS_FOR_CONTENT.lg,
  });

  const userSubmittedAnswers = currentStep?.persistedAnswers ?? {};
  const shouldActionsBeSticky =
    !isEmpty(userSubmittedAnswers) ||
    // @ts-expect-error mode exists on selectable component
    (isSingleComponentOfType('selectable', componentsProps) && componentsProps[0]?.mode === 'multiple') ||
    checkEveryComponentOfType('content', componentsProps);

  if (isCurrentStepFrameLandingPage) {
    return (
      <Flex flex={1} flexDirection="column">
        <LandingPage {...currentStepFrame} performAction={performAction} />
      </Flex>
    );
  }

  return (
    <Flex className={footerContainer({ hasFooter })} flex={1} flexDirection="column" ref={containerRef}>
      {/* Compliance request: added h1 element for wizard */}
      <VisuallyHidden.Root>
        <h1>Lemonade</h1>
      </VisuallyHidden.Root>
      <Flex className={styles.container} flex={1} flexDirection="row" justifyContent="space-between">
        <WizardHeaderSide>
          <WizardHeaderLogo
            coInsurance={currentStepFrame?.coInsurance}
            hideLogo={currentStepFrame == null || currentStepFrame.header?.hideLogo}
            logoAction={
              currentStepFrame != null
                ? currentStepFrame.header?.actions == null
                  ? defaultHeaderActions.left
                  : currentStepFrame.header.actions.left
                : undefined
            }
            performAction={performAction}
          />
        </WizardHeaderSide>

        <WizardLayoutContainer columns={styles.MAIN_GRID_COLUMNS} />

        <WizardHeaderSide>
          <WizardHeaderActions
            actions={headerFilteredActions}
            performAction={performAction}
            predefinedActions={predefinedActions}
            setShowFreeTextInput={setShowFreeTextInput}
            showFreeTextInput={showFreeTextInput}
          />
        </WizardHeaderSide>

        <Flex justifyContent="center" left="0" position="fixed" ref={containerRef} top="0" width="100vw">
          <WizardMain
            containerRef={containerRef}
            currentStepIndex={currentStepIndex}
            frameType={delayedCurrentStepFrameType}
            historyLength={history.length}
            ref={mainRef}
            scrollTrackingTargetRef={historyRef}
          >
            <AnimatePresence>
              {currentStepFrame?.backgroundImage != null && !isTransitioningOut && (
                <WizardBackgroundImage
                  animationDelay={componentAnimationDelay + pageFrameTransitionDelay}
                  backgroundImage={currentStepFrame.backgroundImage}
                  historyVisible={history.length > 0}
                  lastComponentRef={componentRef}
                />
              )}
            </AnimatePresence>
            <WizardFrame
              {...currentStepFrame}
              animationDelay={componentAnimationDelay + pageFrameTransitionDelay}
              answers={currentStepAnswers}
              disabled={isDisabled}
              hasActions={hasActions}
              hasFooter={hasFooter}
              isTransitioningBetweenScripts={isTransitioningBetweenScripts}
              loading={isLoading || isWaitingForInternalProcess}
              onAction={performAction}
              onOverlayClick={scrollToContent}
              ref={frameRef}
              showFreeTextInput={showFreeTextInput}
              type={delayedCurrentStepFrameType}
            >
              <WizardFrameStepAnimator
                isTransitioningOut={isTransitioningOut}
                lastTransitioningFrameType={lastTransitioningFrameType.current}
                onAnimationEnd={() => {
                  presentationLock.current = false;
                  isTransitioningForward.current = false;
                  enableScrollSnap([ScrollSnapItem.Main, ScrollSnapItem.ScriptTransition]);
                }}
                outOfScreen={historyIsDisabled || transitionTriggerCallback.current != null}
                stepId={currentStep?.id}
              >
                <Flex justifyContent="flex-start">
                  <AnimatePresence mode="popLayout">
                    {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
                    {isAvatarVisible && (currentStepFrame as UniclientMessageFrame)?.avatar !== 'hidden' && (
                      <motion.div
                        animate={{
                          opacity: 1,
                          transition: { delay: AVATAR_ANIMATION_DELAY, duration: AVATAR_ANIMATION_DURATION },
                        }}
                        exit={{ opacity: 0 }}
                        initial={{ opacity: 0 }}
                        layout="position"
                        style={{ lineHeight: 0, marginBottom: spacing.s08 }}
                      >
                        <WizardAvatar
                          avatar={
                            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                            (currentStepFrame as UniclientMessageFrame)?.avatar as Exclude<
                              UniclientMessageFrame['avatar'],
                              'hidden'
                            >
                          }
                          size="small"
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Flex>
                <WizardComponent
                  hideTitle={delayedCurrentStepFrameType === 'page'}
                  historyOutput={
                    delayedCurrentStepFrameType === 'page'
                      ? undefined
                      : getMappedInterpolation(currentStepData?.output, allAnswers, i18nContext)
                  }
                  isTransitioningOut={isTransitioningOut}
                  loading={isLoadingComponents}
                  onAction={performAction}
                  onTransitionEnd={() => {
                    submissionTriggerCallback.current?.();
                    transitionTriggerCallback.current?.();
                  }}
                  // for some reason, the page title is persisting the last message step title when transitioning between scripts
                  // this is a workaround to postpone the title change until the page frame transition is complete
                  // TODO: find the root cause and revert to currentStep?.id
                  stepId={delayedCurrentStepId}
                  title={currentStepFrame?.title}
                  titleExitDelay={pageFrameTransitionDelay}
                >
                  <Flex flex="1" flexDirection="column" gap={spacing.s40} id={WIZARD_COMPONENT_CONTAINER_ELEMENT_ID}>
                    {components.length > 0 &&
                      components.map((component, index) => {
                        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                        if (component == null || componentsProps[index] == null) return null;

                        const componentId = componentsProps[index].id ?? COMPONENT_ID;
                        const Component = component as UniclientComponentAdapterFunctionalComponent;
                        const columnsFromProps =
                          'columns' in componentsProps[index]
                            ? (componentsProps[index] as { columns?: MdLgColumns }).columns
                            : undefined;
                        const gridColumn =
                          columnsFromProps != null ? getGridColumn(columnsFromProps) : wizardLayoutColumns;

                        return (
                          <WizardLayoutContainer
                            columns={gridColumn}
                            // eslint-disable-next-line react/no-array-index-key
                            key={`${currentStep?.name}-${componentsProps[index].type}-${index}`}
                          >
                            <Layout
                              gridColumn={{
                                sm: `span ${gridColumn.sm}`,
                                md: `span ${gridColumn.md}`,
                                lg: `span ${gridColumn.lg}`,
                              }}
                              ref={componentRef}
                            >
                              <Component
                                animationDelay={componentAnimationDelay + pageFrameTransitionDelay}
                                answers={currentStepAnswers}
                                answersSchema={mergedSchema}
                                canSubmit={canSubmit}
                                componentProps={componentsProps[index]}
                                disabled={isDisabled}
                                failingValidations={failingValidations}
                                id={componentId}
                                loading={isLoading || isWaitingForInternalProcess}
                                onAction={performAction}
                                onChange={updateAnswers}
                                onInternalProcessError={setCustomError}
                                onSubmit={() => void performAction(currentStepFrame?.action)}
                                onToggleInternalProcess={setWaitingForInternalProcess}
                                onUiAttrsChange={updateUiAttrs}
                                setActionsHidden={setActionsHidden}
                                showError={hasFailingStrictSubmissionValidations}
                                stepId={currentStep?.id}
                                uiAttrs={uiAttrs}
                              />
                            </Layout>
                          </WizardLayoutContainer>
                        );
                      })}
                    <AnimatePresence>
                      {showDisclaimerBeforeActions && disclaimer != null && (
                        <WizardLayoutContainer
                          columns={wizardLayoutColumns}
                          // eslint-disable-next-line react/no-array-index-key
                          key={`${currentStep?.name}-disclaimer`}
                        >
                          <Layout
                            gridColumn={{
                              sm: `span ${DEFAULT_COLUMNS.sm}`,
                              md: `span ${DEFAULT_COLUMNS.md}`,
                              lg: `span ${DEFAULT_COLUMNS.lg}`,
                            }}
                            ref={componentRef}
                          >
                            <WizardDisclaimer
                              animationDelay={
                                componentAnimationDelay + pageFrameTransitionDelay + DISCLAIMER_ANIMATION_DELAY
                              }
                              answers={stepAnswers}
                              extraMargin
                              onAction={performAction}
                              text={disclaimer}
                            />
                          </Layout>
                        </WizardLayoutContainer>
                      )}
                    </AnimatePresence>
                  </Flex>
                </WizardComponent>
              </WizardFrameStepAnimator>
              <Layout
                animate={showFreeTextInput ? 'hidden' : 'visible'}
                as={motion.div}
                bottom={shouldActionsBeSticky ? 0 : 'auto'}
                initial="visible"
                pointerEvents={showFreeTextInput ? 'none' : 'auto'}
                position={shouldActionsBeSticky ? 'sticky' : 'static'}
                variants={wizardActionsAnimationVariants}
              >
                <WizardLayoutContainer columns={wizardLayoutColumns} key={`${currentStep?.name}-actions`}>
                  <Layout
                    gridColumn={{
                      sm: `span ${DEFAULT_COLUMNS.sm}`,
                      md: `span ${DEFAULT_COLUMNS.md}`,
                      lg: `span ${DEFAULT_COLUMNS.lg}`,
                    }}
                  >
                    <WizardActions
                      animationDelay={componentAnimationDelay + pageFrameTransitionDelay}
                      answers={stepAnswers}
                      currentStepIndex={currentStepIndex}
                      disabled={!canSubmit}
                      disclaimer={showDisclaimerAfterActions ? disclaimer : undefined}
                      explanationContent={currentStepFrame?.explanation}
                      hasFooter={hasFooter}
                      hidden={
                        !hasActions ||
                        actionsHidden ||
                        isTransitioningOut ||
                        delayedCurrentStepFrameType === 'page' ||
                        isTransitioningBetweenScripts ||
                        isLoadingComponents
                      }
                      hideBackground={currentStepFrame?.backgroundImage != null}
                      isSticky={shouldActionsBeSticky}
                      key={currentStep?.name}
                      loading={isLoading || isTransitioningOut || isWaitingForInternalProcess}
                      onAction={performAction}
                      primaryAction={currentStepFrame?.action}
                      secondaryAction={currentStepFrame?.secondaryAction}
                      shouldPadTop={components.length > 0 && (currentStepFrame?.explanation == null || isLoading)}
                    />
                  </Layout>
                </WizardLayoutContainer>
              </Layout>
            </WizardFrame>

            <WizardHistory
              animationDelay={componentAnimationDelay + pageFrameTransitionDelay}
              containerRef={containerRef}
              disabled={historyIsDisabled || isTransitioningBetweenScripts}
              editEnabled={!isSealed}
              editingEntryId={editingEntryId}
              history={history}
              isTransitioningOut={isTransitioningOut}
              onEdit={onHistoryEdit}
              onEditComplete={() => {
                enableScrollSnap(ScrollSnapItem.History);
              }}
              ref={historyRef}
              scrollToHistory={scrollToHistory}
            />
          </WizardMain>
          <FreeTextInputController
            currentStepAnswers={currentStepAnswers}
            isLoading={isLoading || isWaitingForInternalProcess || isTransitioningOut || isSubmittingLLMRequest}
            isVisible={showFreeTextInput}
            onClickOutside={handleClickOutside}
            performAction={performAction}
            placeholder="Ask anything..."
            updateAnswers={updateAnswers}
          />
        </Flex>

        {isBelowDesktopViewport && currentStepFrame != null && (
          <WizardMobileHeader
            actions={headerFilteredActions}
            coInsurance={currentStepFrame.coInsurance}
            currentStepIndex={currentStepIndex}
            hideLogo={currentStepFrame.header?.hideLogo}
            logoAction={
              currentStepFrame.header?.actions == null
                ? defaultHeaderActions.left
                : currentStepFrame.header.actions.left
            }
            mobileCenter={currentStepFrame.header?.mobile?.center}
            performAction={performAction}
            predefinedActions={predefinedActions}
            progress={currentStepFrame.progress}
            setShowFreeTextInput={setShowFreeTextInput}
            showFreeTextInput={showFreeTextInput}
            steps={resolvedSteps}
            title={currentStepFrame.header?.title}
          />
        )}
      </Flex>

      <div className={styles.footer}>
        <WizardFooter
          coInsurance={currentStepFrame?.coInsurance}
          expanded={
            currentStepFrame?.coInsurance != null && currentStepFrame.coInsurance.position === 'bottom'
              ? currentStepFrame.coInsurance.expanded
              : undefined
          }
          hidden={showFreeTextInput}
          onAction={performAction}
        />
      </div>
      {currentStepFrame != null && 'survey' in currentStepFrame && currentStepFrame.survey != null && (
        <WizardSurvey openSurveyDialog={openSurveyDialog} stepAnswers={stepAnswers} {...currentStepFrame.survey} />
      )}
    </Flex>
  );
};
